import React, { useEffect, useId, useState, useCallback } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import { PortableText } from '@portabletext/react';
import type { ArbitraryTypedObject } from '@portabletext/types';
import { PortableTextBlock } from '@sanity/types';
import { SlArrowRightCircle } from 'react-icons/sl';
import { BiExpandAlt } from 'react-icons/bi';
import Image from 'next/image';
import { PortableTextWrapper } from '../PortableTextWrapper';
interface IMediaModal {
  imgSrc?: string;
  videoSrc?: string;
  className?: string;
  outsideDescription?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  buttonLabel?: string;
  isObjectFitCover?: boolean;
  description?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  title?: string;
}

const TRANSITION = {
  type: 'spring',
  duration: 0.5,
};

const BACKDROP_CLASS =
  'fixed inset-0 h-full w-full dark:bg-black/25 bg-white/95 backdrop-blur-sm';
const DIALOG_CLASS_FOR_VIDEO =
  'pointer-events-auto relative flex flex-col overflow-hidden bg-black border sm:w-[80%] text-white';

const DIALOG_CLASS_FOR_IMAGE =
  'pointer-events-auto relative flex flex-col overflow-hidden bg-gray-100 border w-[100%] h-[100%] sm:w-[80%] sm:h-[90%]';

function handleKeyDown(
  event: KeyboardEvent,
  setIsMediaModalOpen: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (event.key === 'Escape') {
    setIsMediaModalOpen(false);
  }
}

function handleBodyClass(isOpen: boolean) {
  if (isOpen) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
}

function MediaModal({
  imgSrc,
  videoSrc,
  outsideDescription,
  buttonLabel,
  isObjectFitCover,
  description,
  title,
  className,
}: IMediaModal) {
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  //const isDesktop = useMediaQuery('(min-width:768px)');
  const uniqueId = useId();

  useEffect(() => {
    handleBodyClass(isMediaModalOpen);
    const keyDownHandler = (event: KeyboardEvent) =>
      handleKeyDown(event, setIsMediaModalOpen);

    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [isMediaModalOpen]);

  const openModal = useCallback(() => setIsMediaModalOpen(true), []);
  const closeModal = useCallback(() => setIsMediaModalOpen(false), []);

  const renderEnlargeVideoButton = (buttonLabel: string) => {
    const isDefaultLabel =
      !buttonLabel ||
      buttonLabel === 'Enlarge Video' ||
      buttonLabel === 'Enlarge Demo';

    return (
      <div
        className={`mt-4 justify-start text-primary gap-x-1 items-center cursor-pointer ${isDefaultLabel ? 'flex flex-row' : ''}`}
      >
        {isDefaultLabel && <BiExpandAlt className="font-bold" />}
        <span>{buttonLabel || 'Enlarge Video'}</span>
        {!isDefaultLabel && (
          <SlArrowRightCircle className="text-primary mt-1 block" size={18} />
        )}
      </div>
    );
  };

  const renderEnlargeImageButton = (buttonLabel: string) => {
    return (
      <div
        className={`mt-4 justify-start text-primary gap-x-1 items-center cursor-pointer flex flex-row`}
      >
        <BiExpandAlt className="font-bold" />
        <span>{buttonLabel || 'Enlarge Photo'}</span>
      </div>
    );
  };

  const renderMediaContent = useCallback(
    (showDescription: boolean) => {
      return (
        <>
          {imgSrc && (
            <>
              <motion.div
                layoutId={`dialog-img-${uniqueId}`}
                className={`${isObjectFitCover || isMediaModalOpen ? 'h-full ' : ''}`}
              >
                <Image
                  width={1000}
                  height={1000}
                  src={imgSrc}
                  alt="Media content"
                  className={`${isMediaModalOpen ? 'mx-auto' : ''} ${isObjectFitCover ? 'w-full object-cover h-full ' : 'h-[100%] max-h-[84vh] --w-auto object-contain'}`}
                />
              </motion.div>
              {!isMediaModalOpen &&
                buttonLabel &&
                renderEnlargeImageButton(buttonLabel)}
            </>
          )}
          {videoSrc && (
            <>
              <motion.div
                layoutId={`dialog-video-${uniqueId}`}
                className="w-full h-full"
              >
                <video
                  autoPlay
                  muted
                  controls={isMediaModalOpen}
                  loop
                  className="h-full w-full object-cover rounded-sm"
                >
                  <source src={videoSrc} type="video/mp4" />
                </video>
              </motion.div>
              {showDescription && (
                <>
                  <div className={`mt-2 font-medium`}>
                    {outsideDescription && (
                      <PortableText value={outsideDescription} />
                    )}
                  </div>
                  {!isMediaModalOpen &&
                    buttonLabel &&
                    renderEnlargeVideoButton(buttonLabel)}
                </>
              )}
            </>
          )}
        </>
      );
    },
    [
      imgSrc,
      videoSrc,
      uniqueId,
      isMediaModalOpen,
      isObjectFitCover,
      outsideDescription,
      buttonLabel,
    ]
  );
  if (!imgSrc && !videoSrc) return null;
  return (
    <>
      <MotionConfig transition={TRANSITION}>
        <motion.div
          className={`w-full h-full flex relative flex-col overflow-hidden ${className}`}
          layoutId={`dialog-${uniqueId}`}
          style={{ borderRadius: '0px' }}
          onClick={openModal}
        >
          {renderMediaContent(true)}
        </motion.div>
        {createPortal(
          <AnimatePresence initial={false} mode="sync">
            {isMediaModalOpen && (
              <>
                <motion.div
                  key={`backdrop-${uniqueId}`}
                  className={BACKDROP_CLASS}
                  variants={{ open: { opacity: 1 }, closed: { opacity: 0 } }}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  onClick={closeModal}
                />
                <motion.div
                  key="dialog"
                  className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
                >
                  <motion.div
                    className={
                      videoSrc ? DIALOG_CLASS_FOR_VIDEO : DIALOG_CLASS_FOR_IMAGE
                    }
                    layoutId={`dialog-${uniqueId}`}
                    tabIndex={-1}
                    style={{ borderRadius: '0px' }}
                  >
                    <div className="flex flex-1 flex-col lg:flex-row justify-center p-6">
                      <div className="min-w-[72%]">
                        {title && (
                          <h5 className="text-xl font-medium pb-4">{title}</h5>
                        )}
                        <div>{renderMediaContent(false)}</div>
                      </div>
                      {description && (
                        <div className="min-w-[28%] pt-10 pl-6 pr-2 pb-0 overflow-y-auto">
                          <PortableTextWrapper
                            value={description}
                            textColor="white"
                          />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={closeModal}
                      className="absolute right-3 top-3 p-3 text-zinc-50 cursor-pointer dark:bg-gray-900 bg-gray-400 hover:bg-gray-500 rounded-full dark:hover:bg-gray-800"
                      type="button"
                      aria-label="Close dialog"
                    >
                      <XIcon size={24} />
                    </button>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
      </MotionConfig>
    </>
  );
}

export default MediaModal;
